import { useEffect, useState } from 'react';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { addPageAction } from '../newrelic';

export default function Health() {
  const [health, setHealth] = useState(null);
  const [liveness, setLiveness] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    const [h, l, r] = await Promise.allSettled([
      api.getHealth(),
      api.getLiveness(),
      api.getReadiness(),
    ]);
    if (h.status === 'fulfilled') setHealth(h.value);
    if (l.status === 'fulfilled') setLiveness(l.value);
    if (r.status === 'fulfilled') setReadiness(r.value);
  };

  useEffect(() => {
    addPageAction('health_page_viewed');
    fetchAll().finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    addPageAction('health_refreshed');
    await fetchAll();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Health</h1>
          <p className="page-subtitle">System health checks and component status</p>
        </div>
        <button className="btn btn-outline" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? <span className="spinner" /> : null}
          Refresh
        </button>
      </div>

      <div className="grid-3" style={{ marginBottom: '1.5rem' }}>
        <div className="card">
          <div className="stat-label">Overall Health</div>
          <div style={{ marginTop: '0.5rem' }}>
            <StatusBadge status={health?.status ?? 'UNKNOWN'} />
          </div>
          {health?.version && (
            <div className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
              Version: {health.version}
            </div>
          )}
        </div>
        <div className="card">
          <div className="stat-label">Liveness Probe</div>
          <div style={{ marginTop: '0.5rem' }}>
            <StatusBadge status={liveness?.status ?? 'UNKNOWN'} />
          </div>
          <div className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
            GET /health/live
          </div>
        </div>
        <div className="card">
          <div className="stat-label">Readiness Probe</div>
          <div style={{ marginTop: '0.5rem' }}>
            <StatusBadge status={readiness?.status ?? 'UNKNOWN'} />
          </div>
          <div className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
            GET /health/ready
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Component Details</div>
        {health?.components ? (
          <table style={{ marginTop: '1rem' }}>
            <thead>
              <tr>
                <th>Component</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(health.components).map(([name, comp]) => (
                <tr key={name}>
                  <td style={{ fontWeight: 500, textTransform: 'capitalize' }}>{name}</td>
                  <td>
                    <StatusBadge status={comp.status} />
                  </td>
                  <td className="text-muted">{comp.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted" style={{ marginTop: '0.75rem' }}>
            No component data available
          </p>
        )}
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-title">Raw Response</div>
        <pre className="json" style={{ marginTop: '0.5rem' }}>
          {JSON.stringify(health, null, 2)}
        </pre>
      </div>
    </>
  );
}
